import { createContext, ReactNode, useContext, useEffect, useMemo, useRef } from 'react'
import { observer } from 'mobx-react-lite'

import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'

import { PostPreviewStore, PostPreviewScreens, ScheduledFlowMode } from './PostPreviewStore'
import { PostOptionsPage, PostPreviewChannelType } from 'components/PostPreviewModal/Types'
import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'

import { AlmanacFile } from 'types'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'

type Ctx = { store: PostPreviewStore }
export const PostPreviewStoreContext = createContext<Ctx | null>(null)

export type ExistingScheduledInfo = {
  fileId: string
  postAt: string
  provider: ExternalIntegrationEnum
  externalIntegration?: ExternalIntegration
  status: PostStatusPropertyValue
}

const isScheduled = (s: PostStatusPropertyValue) =>
  s === PostStatusPropertyValue.AutoPostScheduled ||
  s === PostStatusPropertyValue.ReminderScheduled ||
  s === PostStatusPropertyValue.Posted

export const PostPreviewStoreProvider = observer(function PostPreviewStoreProvider({
  channel,
  target,
  initialBrandKitId,
  initialPage,
  children,
  onFilesCreated,
  existingScheduledInfo,
  initialFlow,
}: {
  channel?: PostPreviewChannelType
  target: 'text' | 'design'
  initialBrandKitId?: number
  initialPage?: PostOptionsPage
  children: ReactNode
  onFilesCreated?: (files: AlmanacFile[]) => void
  existingScheduledInfo?: ExistingScheduledInfo
  initialFlow?: ScheduledFlowMode
}) {
  const storeRef = useRef<PostPreviewStore>()
  if (!storeRef.current) {
    storeRef.current = new PostPreviewStore({
      channel,
      target,
      selectedBrandKitId: initialBrandKitId,
      onFilesCreated,
      flow: initialFlow,
    })

    if (existingScheduledInfo && isScheduled(existingScheduledInfo.status)) {
      storeRef.current.initialiseWithExistingScheduledPost(existingScheduledInfo)
    }

    if (initialPage) {
      switch (initialPage) {
        case PostOptionsPage.Schedule:
          if (storeRef.current.flow === ScheduledFlowMode.EDIT) {
            storeRef.current.startEditSchedule()
          } else {
            storeRef.current.setScreen(PostPreviewScreens.AUTOPOST_CALENDAR_SCREEN)
          }
          break
        case PostOptionsPage.Reminder:
          storeRef.current.setScreen(PostPreviewScreens.REMINDER_CALENDAR_SCREEN)
          break
      }
    }

    if (initialFlow) {
      if (initialFlow === ScheduledFlowMode.CROSSPOST) {
        storeRef.current.startCrossPost()
      }
    }
  }

  const store = storeRef.current

  const { data: singleKit } = useBrandKit(store.selectedBrandKitId)
  useEffect(() => {
    if (!singleKit?.brand_kit || !channel) return

    const kit = singleKit.brand_kit
    const provider = providerFromChannelType[channel]

    if (store.flow !== ScheduledFlowMode.CROSSPOST) {
      store.setSelectedAccounts(kit.external_integrations.filter((i) => i.provider === provider))
    }

    store.selectedChannels = new Set(kit.external_integrations.map((i) => i.provider))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleKit, channel])

  const { data: allKits } = useBrandKits()
  useEffect(() => {
    if (store.selectedBrandKitId || !allKits?.length) return
    store.setBrandKitId(allKits[0].id)
  }, [allKits, store])

  const ctx = useMemo(() => ({ store }), [store])
  return <PostPreviewStoreContext.Provider value={ctx}>{children}</PostPreviewStoreContext.Provider>
})

export const usePostPreviewStore = () => {
  const ctx = useContext(PostPreviewStoreContext)
  if (!ctx) throw new Error('PostPreviewStoreProvider missing')
  return ctx.store
}
